'use client';

import { useQuery } from '@tanstack/react-query';
import type { EffectivePermission } from '@westmont/contracts';
import { useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { MY_PERMISSIONS_QUERY_KEY } from '@/lib/query-keys';

/** Re-exported so existing importers keep their path; owned by `query-keys.ts`. */
export { MY_PERMISSIONS_QUERY_KEY };

/**
 * `GET /me/permissions` (US-13 AC2, BR-E03-19) as one shared query.
 *
 * The org sidebar owned this query inline before E15; the accounting module needs
 * the same rows, so both read it from here. The query key stays exactly
 * `['me','permissions']` so the two consumers share a single cache entry and one
 * network request per mount.
 *
 * Fail-open contract, copied from the sidebar it was extracted from: while the
 * request is in flight, and if it fails, `can()` answers true. A permissions
 * outage must not brick a screen, and the nav must not flash empty. Gating turns
 * strict only once the set has really loaded. The BE `PermissionGuard` stays the
 * enforcement point in every case; these helpers only decide affordances.
 *
 * That contract applies to `view` ONLY, since 2026-08-17 (BA bug 14/08, "user
 * có quyền View vẫn edit + delete được"). It used to cover all four actions,
 * which made every Edit/Delete/Add button on the app render ENABLED whenever the
 * set was not `ready` — and `ready` is `isSuccess`, so a `/me/permissions` 500
 * or a malformed body left them enabled permanently, not just for a first paint.
 * A view-only user then saw exactly the write controls BA reported. Reading is
 * the side where an outage must degrade gracefully; writing is the side where an
 * unknown answer must mean "no", so `create`/`edit`/`delete` now fail CLOSED
 * until the real set arrives. The cost is a brief disabled state on first paint,
 * which is the honest render: the app does not yet know the grant.
 */

/** The four flags a `user_permission` row carries. */
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete';

export interface MyPermissions {
  /** Raw rows, empty until the request settles. */
  rows: EffectivePermission[];
  /** Rows by `module_key`, for a call site that needs the flags themselves. */
  map: Map<string, EffectivePermission>;
  /** True once the set has loaded. Gating is only strict from here on. */
  ready: boolean;
  isPending: boolean;
  isError: boolean;
  /** Raw query status, for a caller that must tell "disabled" from "in flight". */
  status: 'idle' | 'pending' | 'error' | 'success';
  /**
   * Row present for `moduleKey` AND its flag set. An absent row means no grant
   * (`permissions.service.ts` returns only granted rows for an org user, and the
   * whole catalog for an org admin). While `!ready` it answers true for `view`
   * and false for `create`/`edit`/`delete` (see the fail-open note above).
   */
  can: (moduleKey: string, action: PermissionAction) => boolean;
}

/**
 * `enabled` exists for one caller: `AuthBoundary` runs above /login, and a
 * request fired there would only ever 401. Every other call site takes the
 * default, so the sidebar and the accounting gates still share one request.
 */
export function useMyPermissions({ enabled = true }: { enabled?: boolean } = {}): MyPermissions {
  const query = useQuery({
    enabled,
    queryKey: MY_PERMISSIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await api.permissions.myPermissions();
      if (res.status !== 200) throw new Error('Failed to load permissions');
      // A 200 whose body is not the contract's array is not a permission set.
      // Real case, dev 2026-08-11: the bundle was built with an empty
      // NEXT_PUBLIC_API_URL, so this request went same-origin and nginx answered
      // the SPA's `/404.html` with HTTP 200 + text/html. ts-rest handed the page
      // back as a STRING, `rows` became that string, and the nav blew up on
      // `rows.some is not a function` — a crash instead of the fail-open the
      // contract below promises. Treat a wrong shape as a failed request.
      if (!Array.isArray(res.body)) throw new Error('Malformed permissions response');
      return res.body;
    },
  });

  const rows = query.data ?? [];
  const map = useMemo(() => {
    const byKey = new Map<string, EffectivePermission>();
    for (const row of query.data ?? []) byKey.set(row.module_key, row);
    return byKey;
  }, [query.data]);

  const ready = query.isSuccess;

  const can = useCallback(
    (moduleKey: string, action: PermissionAction) => {
      // Unknown set: reading stays open (nav, screens), writing is refused.
      if (!ready) return action === 'view';
      const row = map.get(moduleKey);
      if (!row) return false;
      if (action === 'view') return row.can_view;
      if (action === 'create') return row.can_create;
      if (action === 'edit') return row.can_edit;
      return row.can_delete;
    },
    [ready, map],
  );

  return {
    rows,
    map,
    ready,
    isPending: query.isPending,
    isError: query.isError,
    // A DISABLED query reports `pending` in React Query v5, which reads as "in
    // flight" and would hold the auth boundary on a spinner for ever. Report the
    // gate itself instead, so a caller can tell "never asked" from "asking".
    status: enabled ? query.status : 'idle',
    can,
  };
}
